/*
 * Rules for the Mail page (V53, Roadmap 1840): folders, the reading pane,
 * list rows, the AI tags and inbox brief, and remembering AI results so each
 * email is read by the AI once.
 */
import moment from "moment/moment";

import { Email } from "../../../factories/emails/models/emails.models";
import {
  AiEmailInsights,
  AiEmailIntent,
  AiEmailListItem,
  AiEmailMessage,
  AiEmailTriageItem,
} from "../../../slices/AiSlice";
import { TFolder } from "../../../types/types";

// ---------- Folders ----------

export type FolderKind =
  | "inbox"
  | "starred"
  | "sent"
  | "drafts"
  | "spam"
  | "archive"
  | "trash"
  | "other";

export type MailFolder = {
  /** Mailbox folder id ("" for Starred, which is a filter). */
  id: string;
  kind: FolderKind;
  label: string;
  unread: number;
  total: number;
};

const kindOf = (folder: TFolder): FolderKind => {
  const attrs = (
    Array.isArray(folder.attributes)
      ? folder.attributes
      : [folder.attributes || ""]
  )
    .join(" ")
    .toLowerCase();
  const name = (folder.name || "").toLowerCase();
  if (name === "inbox" || attrs.includes("\\inbox")) return "inbox";
  if (attrs.includes("\\sent") || /^sent( items| mail)?$/.test(name))
    return "sent";
  if (attrs.includes("\\drafts") || name === "drafts") return "drafts";
  if (
    attrs.includes("\\junk") ||
    attrs.includes("\\spam") ||
    /^(junk|junk e-?mail|spam)$/.test(name)
  )
    return "spam";
  if (attrs.includes("\\archive") || name === "archive") return "archive";
  if (attrs.includes("\\trash") || /^(trash|deleted items|bin)$/.test(name))
    return "trash";
  return "other";
};

const LABELS: Record<Exclude<FolderKind, "other">, string> = {
  inbox: "Inbox",
  starred: "Starred",
  sent: "Sent",
  drafts: "Drafts",
  spam: "Spam",
  archive: "Archive",
  trash: "Deleted",
};

const ORDER: FolderKind[] = [
  "inbox",
  "starred",
  "sent",
  "drafts",
  "spam",
  "archive",
  "trash",
];

/**
 * The standard folder names, shown greyed out while the mailbox isn't
 * connected (or can't be reached) so the left column is never empty.
 */
export const DEFAULT_FOLDERS: MailFolder[] = ORDER.map((kind) => ({
  id: "",
  kind,
  label: LABELS[kind as Exclude<FolderKind, "other">],
  unread: 0,
  total: 0,
}));

/** Main folders first (Inbox, Starred, Sent, Drafts, Spam, Archive, Deleted), then the rest A–Z. */
export const buildFolders = (
  folders: TFolder[],
): { main: MailFolder[]; more: MailFolder[] } => {
  const main: MailFolder[] = [];
  const more: MailFolder[] = [];
  const seen = new Set<FolderKind>();
  folders.forEach((f) => {
    const kind = kindOf(f);
    const item: MailFolder = {
      id: f.id,
      kind,
      label: kind === "other" ? f.name : LABELS[kind],
      unread: f.unread_count || 0,
      total: f.total_count || 0,
    };
    if (kind === "other" || seen.has(kind)) more.push(item);
    else {
      seen.add(kind);
      main.push(item);
    }
  });
  if (main.some((f) => f.kind === "inbox"))
    main.push({
      id: "",
      kind: "starred",
      label: LABELS.starred,
      unread: 0,
      total: 0,
    });
  main.sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  more.sort((a, b) => a.label.localeCompare(b.label));
  return { main, more };
};

// ---------- Rows ----------

/** "9:42 am" today, "Yesterday", "Oct 18" this year, "Oct 18, 2025" before. */
export const listDate = (unix: number, now: moment.Moment = moment()) => {
  const d = moment.unix(unix || 0);
  if (d.isSame(now, "day")) return d.format("h:mm a");
  if (d.isSame(now.clone().subtract(1, "day"), "day")) return "Yesterday";
  if (d.isSame(now, "year")) return d.format("MMM D");
  return d.format("MMM D, YYYY");
};

export const fullDate = (unix: number) =>
  moment.unix(unix || 0).format("MMM D, YYYY, h:mm a");

export const senderOf = (email: Email) => email.from?.[0];

export const displayName = (p?: { name?: string; email?: string }) =>
  (p?.name || "").trim() || p?.email || "Unknown sender";

/** The first person the email went to (Sent / Drafts rows show "To: …"). */
export const recipientOf = (email: Email) => email.to?.[0];

export const isOutgoingFolder = (kind?: FolderKind) =>
  kind === "sent" || kind === "drafts";

/**
 * Addresses that make an email a "client" email for the Clients filter (QA
 * batch 19, MAIL-009): the sender for received mail; in Sent / Drafts the
 * people it went to (To, Cc, Bcc), because the sender there is me. Lower
 * case, each once, without my own address(es).
 */
export const clientAddressesOf = (
  email: Email,
  outgoing: boolean,
  myAddresses: (string | null | undefined)[] = [],
): string[] => {
  const me = new Set(
    myAddresses.map((a) => (a || "").trim().toLowerCase()).filter(Boolean),
  );
  const people = outgoing
    ? [...(email.to || []), ...(email.cc || []), ...(email.bcc || [])]
    : email.from || [];
  const out: string[] = [];
  people.forEach((p) => {
    const a = (p?.email || "").trim().toLowerCase();
    if (a && !me.has(a) && !out.includes(a)) out.push(a);
  });
  return out;
};

/** The Clients filter: any of those addresses belongs to a client of the firm. */
export const isClientEmail = (
  email: Email,
  outgoing: boolean,
  clientAddresses: Set<string>,
  myAddresses: (string | null | undefined)[] = [],
) =>
  clientAddressesOf(email, outgoing, myAddresses).some((a) =>
    clientAddresses.has(a),
  );

/** Addresses to look up among the clients for the emails on screen (max `limit`). */
export const clientLookupAddresses = (
  emails: Email[],
  outgoing: boolean,
  myAddresses: (string | null | undefined)[] = [],
  limit = 60,
): string[] => {
  const out: string[] = [];
  for (const e of emails) {
    for (const a of clientAddressesOf(e, outgoing, myAddresses)) {
      if (out.length >= limit) return out;
      if (!out.includes(a)) out.push(a);
    }
  }
  return out;
};

/** The open email is in the mailbox's Deleted folder (delete = for good). */
export const isInDeleted = (
  email: Pick<Email, "folders"> | null | undefined,
  folders: MailFolder[],
  currentKind?: FolderKind,
) =>
  currentKind === "trash" ||
  (!!email?.folders?.length &&
    folders.some((f) => f.kind === "trash" && email.folders.includes(f.id)));

/** Plain one-line preview from the snippet (or the body without tags). */
export const previewOf = (email: Email, max = 160) => {
  const raw = email.snippet || (email.body || "").replace(/<[^>]+>/g, " ");
  const text = raw
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
};

// ---------- Reading pane (like Outlook: View > Reading pane) ----------

export type ReadingPane = "right" | "bottom" | "off";

export const READING_PANES: {
  key: ReadingPane;
  label: string;
  hint: string;
}[] = [
  {
    key: "right",
    label: "Right",
    hint: "List on the left, email on the right",
  },
  { key: "bottom", label: "Bottom", hint: "List on top, email below" },
  { key: "off", label: "Off", hint: "List only; an email opens full width" },
];

/**
 * Kept in this browser's localStorage as { [userId]: "right" | "bottom" |
 * "off" }. Sign-out clears sessionStorage and cookies, not localStorage, so
 * the choice is still there when the same person signs in again.
 */
export const READING_PANE_KEY = "proc_mail_reading_pane";

const isPane = (value: unknown): value is ReadingPane =>
  value === "right" || value === "bottom" || value === "off";

/** The person's saved choice from the stored JSON; "right" when none. */
export const parseReadingPane = (
  raw: string | null,
  userId: string,
): ReadingPane => {
  try {
    const all = JSON.parse(raw || "null") as Record<string, unknown> | null;
    const value = all && typeof all === "object" ? all[userId] : undefined;
    return isPane(value) ? value : "right";
  } catch {
    return "right";
  }
};

/** The stored JSON with this person's choice set (other people's kept). */
export const withReadingPane = (
  raw: string | null,
  userId: string,
  pane: ReadingPane,
): string => {
  let all: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(raw || "null");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed))
      all = parsed;
  } catch {
    // Broken value: start again.
  }
  return JSON.stringify({ ...all, [userId]: pane });
};

export const loadReadingPane = (userId: string): ReadingPane => {
  try {
    return parseReadingPane(localStorage.getItem(READING_PANE_KEY), userId);
  } catch {
    return "right";
  }
};

export const saveReadingPane = (userId: string, pane: ReadingPane) => {
  if (!userId) return;
  try {
    localStorage.setItem(
      READING_PANE_KEY,
      withReadingPane(localStorage.getItem(READING_PANE_KEY), userId, pane),
    );
  } catch {
    // Storage blocked: the choice just isn't remembered.
  }
};

// ---------- AI tags and the inbox brief ----------

export type IntentMeta = {
  label: string;
  bg: string;
  color: string;
  dot: string;
};

export const INTENTS: Record<AiEmailIntent, IntentMeta> = {
  reply: {
    label: "Needs reply",
    bg: "#FEF3C7",
    color: "#92400E",
    dot: "#F59E0B",
  },
  review: { label: "Review", bg: "#F1EEFF", color: "#5B3FE0", dot: "#7C5CFF" },
  docs: { label: "Documents", bg: "#E0EAFF", color: "#1D4ED8", dot: "#3B82F6" },
  payment: {
    label: "Payment",
    bg: "#E7F7EE",
    color: "#15803D",
    dot: "#22C55E",
  },
  fyi: { label: "FYI", bg: "#F1F2F5", color: "#5B6678", dot: "#A3A9B8" },
};

/** Groups of AI tags (used by the inbox brief and the documents count). */
export type SmartView = "action" | "docs" | "payments" | "fyi";

export const inView = (
  item: Pick<AiEmailTriageItem, "intent" | "hasDocuments"> | undefined,
  view: SmartView,
) => {
  if (!item) return false;
  if (view === "action")
    return item.intent === "reply" || item.intent === "review";
  if (view === "docs") return item.intent === "docs" || item.hasDocuments;
  if (view === "payments") return item.intent === "payment";
  return item.intent === "fyi";
};

export type BriefLine = { id: string; who: string; what: string; dot: string };

/** Up to 3 "needs you" lines for the inbox brief, newest first. */
export const briefLines = (
  emails: Email[],
  tags: Record<string, AiEmailTriageItem>,
  max = 3,
): BriefLine[] =>
  emails
    .filter((e) => inView(tags[e.id], "action") && tags[e.id].brief)
    .slice(0, max)
    .map((e) => ({
      id: e.id,
      who: displayName(senderOf(e)),
      what: tags[e.id].brief,
      dot: INTENTS[tags[e.id].intent].dot,
    }));

// ---------- What the AI gets ----------

export const toAiListItem = (
  email: Email,
  fromClient: boolean,
): AiEmailListItem => ({
  id: email.id,
  from: displayName(senderOf(email)),
  fromEmail: senderOf(email)?.email,
  subject: email.subject || "",
  snippet: previewOf(email, 600),
  date: fullDate(email.date),
  hasAttachments: !!email.attachments?.some((a) => !a.is_inline),
  fromClient,
});

export const toAiMessage = (
  email: Email,
  myEmail?: string,
): AiEmailMessage => ({
  id: email.id,
  from: displayName(senderOf(email)),
  fromEmail: senderOf(email)?.email,
  to: (email.to || []).map((t) => t.email).join(", "),
  date: fullDate(email.date),
  subject: email.subject || "",
  body: email.body || email.snippet || "",
  attachments: (email.attachments || [])
    .filter((a) => !a.is_inline)
    .map((a) => a.filename),
  sent:
    !!myEmail &&
    senderOf(email)?.email?.toLowerCase() === myEmail.toLowerCase(),
});

/** Earlier emails with the same person in what's loaded (newest first, max 5). */
export const earlierWithSamePerson = (
  email: Email,
  loaded: Email[],
  myEmail?: string,
  max = 5,
) => {
  const them = senderOf(email)?.email?.toLowerCase();
  if (!them) return [];
  return loaded
    .filter((e) => e.id !== email.id && e.date <= email.date)
    .filter((e) => {
      const from = senderOf(e)?.email?.toLowerCase();
      const to = (e.to || []).map((t) => t.email?.toLowerCase());
      return (
        from === them || (from === myEmail?.toLowerCase() && to.includes(them))
      );
    })
    .sort((a, b) => b.date - a.date)
    .slice(0, max);
};

/**
 * Read the open email automatically only while the person has AI uses left
 * (keeps 20 of the 100 daily uses for writing); otherwise show a button.
 */
export const shouldAutoRead = (usedToday?: number, limit?: number) =>
  (usedToday ?? 0) < Math.max(0, (limit ?? 100) - 20);

// ---------- Remembering AI results (this browser) ----------

const TRIAGE_KEY = "proc_mail_ai_tags";
const INSIGHTS_KEY = "proc_mail_ai_reads";
const MAX_TAGS = 600;
const MAX_READS = 60;

type Store<T> = { userId: string; items: Record<string, T & { at: number }> };

const load = <T>(key: string, userId: string): Store<T>["items"] => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(key) || "null",
    ) as Store<T> | null;
    return parsed && parsed.userId === userId ? parsed.items || {} : {};
  } catch {
    return {};
  }
};

const save = <T>(
  key: string,
  userId: string,
  items: Store<T>["items"],
  max: number,
) => {
  const kept = Object.entries(items)
    .sort((a, b) => b[1].at - a[1].at)
    .slice(0, max);
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ userId, items: Object.fromEntries(kept) }),
    );
  } catch {
    // Storage full or blocked: results just aren't remembered.
  }
};

export const loadTags = (userId: string) =>
  load<AiEmailTriageItem>(TRIAGE_KEY, userId);

export const saveTags = (
  userId: string,
  items: Record<string, AiEmailTriageItem & { at: number }>,
) => save(TRIAGE_KEY, userId, items, MAX_TAGS);

export const loadReads = (userId: string) =>
  load<AiEmailInsights>(INSIGHTS_KEY, userId);

export const saveReads = (
  userId: string,
  items: Record<string, AiEmailInsights & { at: number }>,
) => save(INSIGHTS_KEY, userId, items, MAX_READS);
